/**
 * formatActionExecutors — les verbes de STRUCTURE et d'habillage du canvas
 * (#992) : `insert_heading`, `insert_page_break`, `add_hyperlink`,
 * `create_table`. La forme du texte (`format_text`) vit dans
 * `formatTextExecutor.ts`.
 *
 * > Au tour 73 de la conversation autopsiée, l'agent doit avouer à l'avocat
 * > qu'il ne peut ni mettre en gras, ni souligner, ni encadrer un titre. Au tour
 * > 85, il simule la mise en forme par des balises `[Titre]` dans le chat et le
 * > renvoie la faire à la main dans Word. Les capacités existaient dans le
 * > produit ; elles n'avaient jamais été exposées à l'agent web.
 *
 * ## Ce qui a été MESURÉ avant d'être exposé
 *
 * Relevé le 2026-08-16 sur `superdoc@2.6.0`
 * (`tests/ws-mocked/mesure-992-verbes-de-mise-en-forme.spec.ts`, projet
 * `mesures`). Rien ici ne vient d'un `.d.ts` ni d'un nom de méthode — les deux
 * ont déjà menti sur ce chantier.
 *
 * | opération | `available` | `tracked` déclaré | révisions OBTENUES |
 * |---|---|---|---|
 * | `create.paragraph` | ✅ | true | 1 |
 * | `styles.paragraph.setStyle` | ✅ | true | 1 |
 * | `format.paragraph.setFlowOptions` | ✅ | true | 1 |
 * | `hyperlinks.wrap` | ✅ | **false** | **1** ⚠️ |
 * | `create.table` | ✅ | true | 1 |
 * | `tables.setCellText` | ✅ | false | **0** |
 *
 * ⚠️ **`hyperlinks.wrap` s'annonce non suivable et produit pourtant une
 * révision** (9 → 10 identités, texte remplacé peint en barré + inséré en
 * bleu). C'est la raison pour laquelle il n'est PAS déclaré en
 * `reviewedOperations` : l'y mettre ferait refuser `add_hyperlink` sous le
 * régime suivi, pour une déclaration que la mesure
 * dément. Le `tracked` déclaré ne promet rien, **dans les deux sens** (#990).
 */

import { CANVAS_FORMAT_REFUSAL_DETAIL_MAX, type CanvasActionInput, type CanvasSelector } from '@hector/shared-types';
import {
  abortedBeforeMutation,
  buildVerifiedReceipt,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type VerificationCheck,
} from './actionReceipt';
import { idsDeRevision, operationDuDocument, readCanvasSnapshot, type CanvasSnapshot, type CanvasSnapshotBlock } from './canvasSnapshot';
import { estArretDeLot } from './executionAsynchrone';
import { muterSousFences, revisionCourante, type RevisionDeDocument } from './fencesDeMutation';
import { messageTargetNotInFinalInspection, resolveCanvasSelector } from './selectorResolver';
import { modeDeRevision, targetSummary, type CanvasActionContext } from './textActionExecutors';

type MethodeProvider = (input?: unknown, options?: unknown) => unknown;

/** Les surfaces du Document API que ces verbes empruntent, toutes mesurées présentes. */
interface SurfacesDeMiseEnForme {
  format?: { paragraph?: { setFlowOptions?: MethodeProvider } };
  create?: { paragraph?: MethodeProvider; table?: MethodeProvider };
  styles?: { paragraph?: { setStyle?: MethodeProvider } };
  hyperlinks?: { wrap?: MethodeProvider; list?: MethodeProvider };
  tables?: {
    get?: MethodeProvider;
    setCellText?: MethodeProvider;
    setStyle?: MethodeProvider;
    setLayout?: MethodeProvider;
    applyBorderPreset?: MethodeProvider;
    getProperties?: MethodeProvider;
  };
}

type ContexteDeMiseEnForme = CanvasActionContext & { doc: CanvasActionContext['doc'] & SurfacesDeMiseEnForme };

export type InsertHeadingInput = Extract<CanvasActionInput, { action: 'insert_heading' }>;

function snapshot(ctx: CanvasActionContext, fenetre = { offset: 0, limit: 80 }): Promise<CanvasSnapshot> {
  return readCanvasSnapshot(ctx.doc, fenetre);
}

/**
 * Résout un sélecteur en UN bloc, ou rend le refus déjà rédigé pour le modèle.
 *
 * Exporté depuis #994 : les verbes de liste et de structure de table résolvent
 * exactement de la même façon, et deux copies de ces messages divergeraient au
 * premier ajustement — c'est le refus que le modèle lit, pas un détail interne.
 */
export async function resoudreLaCible(
  ctx: CanvasActionContext,
  selector: CanvasSelector,
  verbe: string,
): Promise<{ ok: true; target: CanvasSnapshotBlock; candidateCount: number } | { ok: false; code: string; message: string }> {
  const resolution = await resolveCanvasSelector(ctx.doc, selector, {
    requireUnique: true,
    inspectedPublicNodeIds: ctx.inspectedPublicNodeIds,
  });
  if (resolution.ok) return resolution;
  return {
    ok: false,
    code: resolution.code,
    message: resolution.code === 'AMBIGUOUS_SELECTOR'
      ? `${verbe} a été REFUSÉ avant toute écriture : le sélecteur désigne ${resolution.candidateCount} blocs, `
        + 'donc la mise en forme n\'a pas de cible unique. Rien n\'a été modifié. '
        + 'Relance canvas_inspect puis cible par nodeId, ou allonge les termes de recherche jusqu\'à ce qu\'ils ne '
        + 'correspondent qu\'à un seul bloc.'
      : resolution.code === 'TARGET_NOT_IN_FINAL_INSPECTION'
        ? messageTargetNotInFinalInspection()
      : `${verbe} a été REFUSÉ avant toute écriture : aucun bloc ne correspond au sélecteur. `
        + 'Rien n\'a été modifié. Relance canvas_inspect pour lire le document tel qu\'il est MAINTENANT, '
        + 'puis cible un nodeId ou un texte qui s\'y trouve réellement.',
  };
}

/**
 * La cible ÉCRIVABLE d'une portion de texte, résolue juste avant l'écriture.
 *
 * ⚠️ Les handles de texte sont **éphémères et révision-scopés** — le provider le
 * dit lui-même. Chaque mutation faisant avancer la révision, une cible résolue
 * avant une autre écriture rend, selon la surface, un refus explicite ou un
 * `success:true` qui ANNULE l'effet précédent (#979).
 *
 * ⚠️ La forme est `{type, pattern, within}` **à PLAT**, et le retour porte les
 * cibles sous `items[].target` — mesuré, jamais deviné. L'enveloppe
 * `{select:{…}}` que suggèrent les types rend `MATCH_NOT_FOUND` sur un texte
 * pourtant présent, et une clé inventée (`matches`, `ref` racine) rendrait un
 * `undefined` silencieux.
 */
async function cibleEcrivable(
  ctx: CanvasActionContext,
  bloc: CanvasSnapshotBlock,
  motif: string,
): Promise<{ ok: true; target: unknown } | { ok: false; code: string; message: string }> {
  if (typeof ctx.doc.query?.match !== 'function') {
    return {
      ok: false,
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'L\'action exige editor.doc.query.match pour obtenir une cible écrivable dans le bloc résolu ; '
        + 'cet éditeur ne l\'expose pas. Relancer la même action échouera à l\'identique.',
    };
  }
  const resultat = await ctx.doc.query.match({
    type: 'text',
    pattern: motif,
    caseSensitive: true,
    within: { kind: 'block', nodeType: bloc.nodeType, nodeId: bloc.nodeId },
  });
  const items = extraireLesItems(resultat);
  const apercu = motif.length > 80 ? `${motif.slice(0, 80)}…` : motif;
  if (items.length === 0) {
    return {
      ok: false,
      code: 'SELECTOR_NOT_FOUND',
      message: `Le bloc résolu (nodeId=${bloc.nodeId}) ne contient pas la portion de texte visée : `
        + `« ${apercu} ». Soit le texte du bloc a changé depuis sa lecture, soit une différence invisible `
        + '(espace insécable, apostrophe typographique, suppression suivie) empêche l\'appariement. '
        + 'Relance canvas_inspect pour lire le texte ACTUEL du bloc, puis rappelle l\'action avec ce texte exact.',
    };
  }
  if (items.length > 1) {
    return {
      ok: false,
      code: 'AMBIGUOUS_SELECTOR',
      message: `Le bloc résolu (nodeId=${bloc.nodeId}) contient ${items.length} occurrences de « ${apercu} », `
        + 'donc l\'action n\'a pas de cible unique et rien n\'a été modifié. '
        + 'Allonge « text » jusqu\'à une portion de phrase unique dans ce bloc.',
    };
  }
  const target = (items[0] as { target?: unknown }).target;
  return target === undefined || target === null
    ? {
        ok: false,
        code: 'SELECTOR_REF_UNAVAILABLE',
        message: `L'appariement de texte dans le bloc (nodeId=${bloc.nodeId}) ne porte aucune cible écrivable : `
          + 'rien ne peut y être écrit. C\'est une limite du fournisseur sur ce bloc, pas une erreur de ciblage — '
          + 'relancer la même action échouera à l\'identique. Réécris le passage par rewrite_block, puis rappelle l\'action.',
      }
    : { ok: true, target };
}

function extraireLesItems(valeur: unknown): Record<string, unknown>[] {
  if (typeof valeur !== 'object' || valeur === null) return [];
  const items = (valeur as { items?: unknown }).items;
  return Array.isArray(items)
    ? items.filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
    : [];
}

/** `success:false` — la seule forme de refus que le provider publie. */
export function estUnRefus(resultat: unknown): boolean {
  return typeof resultat === 'object' && resultat !== null && (resultat as { success?: unknown }).success === false;
}

/**
 * Le refus du provider rendu lisible, BORNÉ (`CANVAS_FORMAT_REFUSAL_DETAIL_MAX`).
 *
 * La borne est load-bearing : ce détail part dans un receipt lu par un modèle,
 * et un `failure` qui embarquerait un extrait de document entier lui coûterait
 * sa fenêtre de contexte au moment où il doit décider quoi faire.
 */
export function detailDuRefus(resultat: unknown): string {
  return JSON.stringify((resultat as { failure?: unknown } | null)?.failure ?? resultat).slice(0, CANVAS_FORMAT_REFUSAL_DETAIL_MAX);
}

/**
 * Traduit le `placement` du contrat agent en `at` du Document API.
 *
 * Les quatre formes ont été MESURÉES acceptées par `create.paragraph` et
 * `create.table` le 2026-08-16 : `{kind:'documentStart'}`,
 * `{kind:'documentEnd'}`, et `{kind:'before'|'after', target}` où `target` est
 * une adresse de BLOC. Ce n'est pas la même grammaire que `doc.insert`, dont le
 * `placement` vit à côté du `target` — d'où cette traduction plutôt qu'un
 * passage tel quel.
 */
async function resoudreLePlacement(
  ctx: CanvasActionContext,
  placement: InsertHeadingInput['placement'],
  verbe: string,
): Promise<{ ok: true; at: Record<string, unknown>; ancre?: CanvasSnapshotBlock } | { ok: false; code: string; message: string }> {
  if (placement.at === 'document_start') return { ok: true, at: { kind: 'documentStart' } };
  if (placement.at === 'document_end') return { ok: true, at: { kind: 'documentEnd' } };
  const resolue = await resoudreLaCible(ctx, placement.selector, verbe);
  if (!resolue.ok) return resolue;
  return {
    ok: true,
    at: {
      kind: placement.at === 'before' ? 'before' : 'after',
      target: { kind: 'block', nodeType: resolue.target.nodeType, nodeId: resolue.target.nodeId },
    },
    ancre: resolue.target,
  };
}

/** L'identifiant de nœud rendu par un receipt de création, quelle que soit sa clé. */
function nodeIdCree(resultat: unknown, cle: 'paragraph' | 'heading' | 'table'): string | undefined {
  if (typeof resultat !== 'object' || resultat === null) return undefined;
  const cree = (resultat as Record<string, unknown>)[cle];
  if (typeof cree !== 'object' || cree === null) return undefined;
  const nodeId = (cree as { nodeId?: unknown }).nodeId;
  return typeof nodeId === 'string' && nodeId.length > 0 ? nodeId : undefined;
}

/**
 * Insérer un TITRE de niveau 1 à 6 (#992).
 *
 * ## Pourquoi `create.paragraph` + `setStyle`, et pas `create.heading`
 *
 * `create.heading({at, text, level})` existe, est disponible, et produit bien un
 * bloc `nodeType:'heading'` avec le bon `headingLevel` — mesuré le 2026-08-16.
 * Il porte cependant le style BUILT-IN `Heading2`, et le rendu peint le montre :
 * 18,67 px **sans graisse**. Le gabarit Hector, lui, pose `Hector-H2` — 18,67 px
 * **gras et souligné**. Un titre créé par la voie « directe » serait donc
 * visiblement différent des titres que la génération d'acte et `write_markdown`
 * posent dans le même document.
 *
 * Deux opérations plutôt qu'une, donc, et c'est un choix de RENDU, pas une
 * limite du fournisseur : le titre inséré par l'agent doit être indiscernable
 * des autres titres de l'acte.
 *
 * ## La postcondition est une vraie RELECTURE, contrairement aux autres verbes
 *
 * `blocks.list` ne publie plus aucune facette de style (#986) — mais il publie
 * `nodeType`, `styleId` et `headingLevel`, et la mesure le confirme : le bloc
 * relu après `setStyle` passe de `paragraph` à `heading`, avec
 * `styleId:'Hector-H2'` et `headingLevel:2`. C'est donc le document lui-même qui
 * juge ici, pas l'accusé du fournisseur.
 */
export async function executeInsertHeading(
  ctx: ContexteDeMiseEnForme,
  input: InsertHeadingInput,
): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  const placement = await resoudreLePlacement(ctx, input.placement, 'insert_heading');
  if (!placement.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: placement.code,
      message: placement.message,
    });
  }
  if (typeof ctx.doc.create?.paragraph !== 'function' || typeof ctx.doc.styles?.paragraph?.setStyle !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'insert_heading exige editor.doc.create.paragraph et editor.doc.styles.paragraph.setStyle, que cet '
        + 'éditeur n\'expose pas tous les deux. Rien n\'a été écrit et relancer la même action échouera à l\'identique. '
        + 'Utilise insert_paragraphs pour poser le texte, sans niveau de titre.',
    });
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const revisionDeCreation = await revisionCourante(ctx, pre.revision);
  const creation = await muterSousFences(
    ctx,
    'insert_heading:create.paragraph',
    { changeMode: modeDeRevision(ctx), expectedRevision: revisionDeCreation },
    () => ctx.doc.create?.paragraph?.({ at: placement.at, text: input.text }, {
      changeMode: modeDeRevision(ctx),
      expectedRevision: revisionDeCreation,
    }),
  );
  const nodeId = nodeIdCree(creation, 'paragraph');
  if (nodeId === undefined) {
    // ⚠️ Le style ne peut PAS être posé sans identifiant, et le poser « au
    // dernier bloc » viserait à l'aveugle : le paragraphe est écrit, il restera
    // au style du corps. Le receipt doit dire exactement cela plutôt que de
    // laisser croire qu'un titre a été créé.
    const postSansStyle = await snapshot(ctx);
    return buildVerifiedReceipt({
      intent: input.action,
      regimeSuivi: ctx.regimeSuivi,
      preSnapshot: toCanvasSnapshotSummary(pre),
      postSnapshot: toCanvasSnapshotSummary(postSansStyle),
      resolvedTargets: placement.ancre === undefined ? [] : [targetSummary(placement.ancre)],
      provider: creation,
      revisionsAvant,
      revisionsApres: idsDeRevision(postSansStyle.trackedChanges),
      verification: [{
        name: 'heading_node_created',
        passed: false,
        message: 'Le paragraphe a été créé mais l\'éditeur n\'a pas rendu son identifiant, donc le style de titre n\'a '
          + 'pas pu être posé : le texte est dans le document, au style du corps. Relis-le par canvas_inspect puis '
          + 'applique apply_style avec styleId "Hector-H' + input.level + '".',
      }],
      effects: [{ kind: 'insert_heading', reviewability: 'tracked', description: 'Paragraphe inséré, style de titre non posé.' }],
    });
  }

  const styleId = `Hector-H${input.level}`;
  const revisionDeStyle = await revisionCourante(ctx, pre.revision);
  const pose = await muterSousFences(
    ctx,
    'insert_heading:styles.paragraph.setStyle',
    { changeMode: modeDeRevision(ctx), expectedRevision: revisionDeStyle },
    () => ctx.doc.styles?.paragraph?.setStyle?.({
      target: { kind: 'block', nodeType: 'paragraph', nodeId },
      styleId,
    }, { changeMode: modeDeRevision(ctx), expectedRevision: revisionDeStyle }),
  );

  const post = await snapshot(ctx);
  const relu = post.blocks.find((bloc) => bloc.nodeId === nodeId);
  ctx.verifierEpoque('insert_heading:receipt');

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: placement.ancre === undefined ? [] : [targetSummary(placement.ancre)],
    provider: [creation, pose],
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    verification: [
      {
        name: 'heading_text',
        passed: relu?.fullText === input.text,
        message: 'Le bloc créé doit porter exactement le texte demandé.',
      },
      {
        // La seule postcondition de RELECTURE de tout ce module : `blocks.list`
        // ne publie aucune facette de style, mais il publie bien le niveau de
        // titre une fois le style nommé posé (mesuré).
        name: 'heading_level',
        passed: relu?.headingLevel === input.level,
        message: `Le bloc créé doit être relu comme un titre de niveau ${input.level} (style ${styleId}).`,
      },
    ],
    effects: [{
      kind: 'insert_heading',
      reviewability: 'tracked',
      description: `Titre de niveau ${input.level} inséré au style ${styleId}.`,
      nodeIds: [nodeId],
    }],
  });
}

/**
 * Poser un SAUT DE PAGE avant un bloc (#992).
 *
 * ## Pourquoi `setFlowOptions`, et jamais une application de style
 *
 * La cible d'une application de style est un littéral fermé sur les défauts du
 * document : poser le saut par là le poserait avant **tous** les paragraphes.
 * `format.paragraph.setFlowOptions` est ciblé par nœud, et c'est déjà le chemin
 * qu'emprunte la normalisation structurelle de la génération d'acte.
 *
 * ## ⚠️ Aucune relecture ne peut confirmer le saut
 *
 * `pageBreakBefore` sort **toujours `false`** de la projection SDM en 2.6.0
 * (#986) : une postcondition bâtie dessus serait rouge sur un saut pourtant
 * posé. La preuve d'effet est donc l'accusé du fournisseur plus le mouvement
 * des identités de révision — et, hors d'ici, la **pagination peinte**
 * (`tests/helpers/pagination-peinte.ts`), qui est le seul juge qui voie ce que
 * l'avocat voit.
 *
 * ## ⚠️ Le saut se pose EN DERNIER, et la description du verbe le dit
 *
 * Défaut ouvert [#998] : un paragraphe inséré **après** un bloc portant
 * `pageBreakBefore` hérite du saut et n'est peint nulle part — le peintre ouvre
 * une page vide à sa place. Poser le saut avant d'écrire la suite condamne donc
 * cette suite à être invisible à l'écran, alors même que le fichier exporté est
 * correct. C'est un ordonnancement, pas une limite : il appartient à la
 * description que le modèle lit.
 */
export async function executeInsertPageBreak(
  ctx: ContexteDeMiseEnForme,
  input: Extract<CanvasActionInput, { action: 'insert_page_break' }>,
): Promise<CanvasActionReceipt> {
  const resolue = await resoudreLaCible(ctx, input.selector, 'insert_page_break');
  if (!resolue.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(await snapshot(ctx)),
      code: resolue.code,
      message: resolue.message,
    });
  }
  const bloc = resolue.target;
  const pre = await snapshot(ctx, { offset: bloc.ordinal - 1, limit: 1 });

  if (typeof ctx.doc.format?.paragraph?.setFlowOptions !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'insert_page_break exige editor.doc.format.paragraph.setFlowOptions, que cet éditeur n\'expose pas. '
        + 'Rien n\'a été écrit et relancer la même action échouera à l\'identique. '
        + 'Il n\'existe pas d\'autre chemin : un saut de page ne se simule pas par des paragraphes vides, qui se '
        + 'décalent dès que le texte au-dessus change.',
    });
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const attendue = await revisionCourante(ctx, pre.revision);
  const resultat = await muterSousFences(
    ctx,
    'insert_page_break:setFlowOptions',
    { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
    () => ctx.doc.format?.paragraph?.setFlowOptions?.({
      target: { kind: 'block', nodeType: bloc.nodeType, nodeId: bloc.nodeId },
      pageBreakBefore: true,
    }, { changeMode: modeDeRevision(ctx), expectedRevision: attendue }),
  );

  const post = await snapshot(ctx, { offset: bloc.ordinal - 1, limit: 1 });
  ctx.verifierEpoque('insert_page_break:receipt');

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [targetSummary(bloc, resolue.candidateCount)],
    provider: resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    verification: [{
      name: 'page_break_applied',
      passed: !estUnRefus(resultat),
      // ⚠️ Ce contrôle porte sur l'ACCUSÉ, faute de relecture possible, et le
      // message le dit : un lecteur de log ne doit pas croire que le saut a été
      // relu dans le document.
      message: estUnRefus(resultat)
        ? `L'éditeur a refusé de poser le saut de page sur le bloc ${bloc.nodeId} : ${detailDuRefus(resultat)}`
        : 'L\'éditeur a accepté de poser le saut de page. Il ne peut pas être RELU sur ce build (la projection du '
          + 'document ne publie plus les propriétés de paragraphe) : seule la pagination affichée le montre.',
    }],
    effects: [{
      kind: 'insert_page_break',
      reviewability: 'tracked',
      description: 'Saut de page posé avant le bloc résolu.',
      nodeIds: [bloc.nodeId],
    }],
  });
}

/**
 * L'adresse de TEXTE qu'exige `hyperlinks.wrap`, dérivée d'une cible de
 * sélection rendue par `query.match`.
 *
 * ⚠️ Les deux formes ne sont PAS interchangeables, et c'est le provider qui l'a
 * dit : passer la cible de sélection telle quelle rend
 * « hyperlinks.wrap requires a valid TextAddress target with kind 'text',
 * blockId, and range ». La sélection porte `{start:{blockId, offset},
 * end:{offset}}` ; l'adresse de texte veut `{blockId, range:{start, end}}`. La
 * conversion est mécanique, elle n'était simplement écrite nulle part.
 */
function adresseDeTexte(cible: unknown): { kind: 'text'; blockId: string; range: { start: number; end: number } } | undefined {
  if (typeof cible !== 'object' || cible === null) return undefined;
  const { start, end } = cible as { start?: { blockId?: unknown; offset?: unknown }; end?: { offset?: unknown } };
  if (typeof start?.blockId !== 'string' || typeof start.offset !== 'number' || typeof end?.offset !== 'number') {
    return undefined;
  }
  return { kind: 'text', blockId: start.blockId, range: { start: start.offset, end: end.offset } };
}

/**
 * Les cibles externes des hyperliens du document, relues — le juge d'`add_hyperlink`.
 *
 * ⚠️ **Les liens vivent sous `stories[].hyperlinks[]`, PAS sous `items[]`** —
 * relevé sur la réponse brute le 2026-08-16 :
 * `{stories:[{storyId, partUri, hyperlinks:[{externalTarget, text, …}]}, …],
 * evaluatedRevision, total, items:[], page}`. La réponse porte bien un `items`
 * et un `total`, et **ils valent `[]` et `0` alors que les liens existent** :
 * les lire aurait rendu un zéro parfaitement cohérent et entièrement faux, donc
 * une postcondition rouge sur un lien pourtant posé. C'est exactement ce qui
 * s'est produit à la première exécution de la garde en navigateur.
 *
 * Les deux emplacements sont lus, `items` d'abord vide n'étant pas une preuve
 * qu'il le restera sur une version suivante.
 */
async function ciblesDesHyperliens(ctx: ContexteDeMiseEnForme): Promise<string[] | undefined> {
  if (typeof ctx.doc.hyperlinks?.list !== 'function') return undefined;
  const relu = await ctx.doc.hyperlinks.list({ offset: 0, limit: 200 });
  const stories = typeof relu === 'object' && relu !== null && Array.isArray((relu as { stories?: unknown }).stories)
    ? (relu as { stories: unknown[] }).stories
    : [];
  const liens = stories.flatMap((story) => extraireTableau(story, 'hyperlinks'));
  // ⚠️ La clé de cible est `externalTarget` — ni `href`, ni `url`, ni `target`,
  // qui rendraient toutes `undefined` en silence.
  return [...liens, ...extraireLesItems(relu)]
    .flatMap((lien) => typeof lien.externalTarget === 'string' ? [lien.externalTarget] : []);
}

function extraireTableau(valeur: unknown, cle: string): Record<string, unknown>[] {
  if (typeof valeur !== 'object' || valeur === null) return [];
  const tableau = (valeur as Record<string, unknown>)[cle];
  return Array.isArray(tableau)
    ? tableau.filter((entree): entree is Record<string, unknown> => typeof entree === 'object' && entree !== null)
    : [];
}

/**
 * Transformer une portion de texte existante en HYPERLIEN (#992).
 *
 * ## La forme d'entrée a coûté trois refus, et aucun `.d.ts` ne la donnait
 *
 * `{target: TextAddress, link: {destination: {href}}}`. Chaque refus du
 * fournisseur nommait le champ suivant : « requires a link specification
 * object » → il faut un `link` ; « requires a destination object » → sa
 * `destination` est un OBJET, pas une chaîne ; « destination must include href
 * or anchor » → cet objet porte `href`. Les surfaces voisines sont des impasses
 * mesurées : `create.hyperlink` n'existe pas (`CAPABILITY_UNAVAILABLE`),
 * `hyperlinks.patch` est annoncée indisponible, et `format.apply` refuse
 * `link` comme `href` (« Unknown inline property »).
 *
 * ## ⚠️ Le suivi déclaré est FAUX, dans le sens généreux
 *
 * `hyperlinks.wrap` s'annonce `tracked:false` et produit pourtant une révision
 * sous fenêtre `suggesting` (mesuré : 9 → 10 identités, le texte remplacé peint
 * en barré et le lien inséré en bleu). C'est pourquoi elle ne figure pas dans
 * `reviewedOperations` du catalogue : l'y mettre ferait refuser le verbe sous le
 * régime suivi, au nom d'une déclaration que la
 * mesure dément. Le receipt, lui, continue de juger par relecture.
 */
export async function executeAddHyperlink(
  ctx: ContexteDeMiseEnForme,
  input: Extract<CanvasActionInput, { action: 'add_hyperlink' }>,
): Promise<CanvasActionReceipt> {
  const resolue = await resoudreLaCible(ctx, input.selector, 'add_hyperlink');
  if (!resolue.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(await snapshot(ctx)),
      code: resolue.code,
      message: resolue.message,
    });
  }
  const bloc = resolue.target;
  const pre = await snapshot(ctx, { offset: bloc.ordinal - 1, limit: 1 });

  if (!bloc.fullText.includes(input.text)) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'SELECTOR_NOT_FOUND',
      message: `add_hyperlink a été REFUSÉ avant toute écriture : le bloc résolu (nodeId=${bloc.nodeId}) ne contient `
        + `pas « ${input.text} », donc il n'y a pas de texte à transformer en lien. Rien n'a été modifié. `
        + 'Ce verbe LIE un texte déjà présent, il n\'en écrit pas : si le libellé du lien n\'existe pas encore, '
        + 'écris-le d\'abord (insert_paragraphs ou rewrite_block), puis rappelle add_hyperlink dessus.',
    });
  }
  if (typeof ctx.doc.hyperlinks?.wrap !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'add_hyperlink exige editor.doc.hyperlinks.wrap, que cet éditeur n\'expose pas. Rien n\'a été écrit et '
        + 'relancer la même action échouera à l\'identique. Écris l\'adresse en clair dans le texte à la place.',
    });
  }

  const cible = await cibleEcrivable(ctx, bloc, input.text);
  if (!cible.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: cible.code,
      message: cible.message,
    });
  }
  const adresse = adresseDeTexte(cible.target);
  if (adresse === undefined) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'SELECTOR_REF_UNAVAILABLE',
      message: `L'appariement de « ${input.text} » dans le bloc (nodeId=${bloc.nodeId}) n'a pas rendu de plage de texte `
        + 'adressable, et un hyperlien ne peut se poser que sur une plage. Rien n\'a été modifié, et relancer la même '
        + 'action échouera à l\'identique. Écris l\'adresse en clair dans le texte à la place.',
    });
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const attendue = await revisionCourante(ctx, pre.revision);
  const resultat = await muterSousFences(
    ctx,
    'add_hyperlink:hyperlinks.wrap',
    { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
    () => ctx.doc.hyperlinks?.wrap?.({
      target: adresse,
      link: { destination: { href: input.href } },
    }, { changeMode: modeDeRevision(ctx), expectedRevision: attendue }),
  );

  const post = await snapshot(ctx, { offset: bloc.ordinal - 1, limit: 1 });
  const ciblesRelues = await ciblesDesHyperliens(ctx);
  ctx.verifierEpoque('add_hyperlink:receipt');

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [targetSummary(bloc, resolue.candidateCount)],
    provider: resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    verification: [{
      // Vraie postcondition de RELECTURE : `hyperlinks.list` publie les cibles
      // externes du document, donc le lien se prouve sur le document et pas sur
      // l'accusé du fournisseur.
      name: 'hyperlink_present',
      passed: ciblesRelues === undefined ? !estUnRefus(resultat) : ciblesRelues.includes(input.href),
      message: ciblesRelues === undefined
        ? 'L\'éditeur n\'expose pas la relecture des hyperliens : seule sa réponse atteste la pose du lien.'
        : `Le document relu doit porter un hyperlien vers ${input.href}.`,
    }],
    effects: [{
      kind: 'add_hyperlink',
      reviewability: 'tracked',
      description: `Hyperlien posé sur « ${input.text} » vers ${input.href}.`,
      nodeIds: [bloc.nodeId],
    }],
  });
}

/**
 * Créer un TABLEAU et le remplir (#992).
 *
 * ## Le juge est la RELECTURE de la géométrie, jamais le receipt de création
 *
 * `doc.insert` HTML rendait déjà `success:true` en posant… un paragraphe (#980,
 * le canal HTML aplatit les tables). `tables.get({nodeId})` rend `{rows,
 * columns}` : une table annoncée créée qui se relit à une géométrie différente
 * n'est pas la table demandée, et le receipt doit le dire.
 *
 * ## ⚠️ Ce que ce verbe PERD, et pourquoi la description du verbe le dit
 *
 * `tables.setCellText` pose du **texte brut** : gras, italique et hyperliens ne
 * survivent pas dans une cellule, et plusieurs paragraphes de cellule sont
 * aplatis. C'est une limite du fournisseur, pas un choix — mais la taire ferait
 * promettre à l'agent une mise en forme que l'avocat ne verrait jamais. Elle
 * appartient donc au texte que le modèle lit avant d'appeler.
 *
 * Elle est aussi annoncée **non suivable** (`tracked:false`) et mesurée
 * produisant zéro révision, là où `create.table` en produit une : le cadre du
 * tableau est révisable, son contenu ne l'est pas. L'effet est donc déclaré
 * `mixed` — et `mixed` n'est jamais rabattu par le receipt, contrairement à
 * `tracked`, ce qui est précisément ce qu'on veut ici.
 *
 * ## L'habillage : ce qui est du confort, et ce qui est la demande
 *
 * Le style de grille et la pleine largeur sont du confort. Ils ne sont pas
 * déclarés requis au catalogue — les exiger ferait disparaître le verbe ENTIER
 * d'un document qui ne saurait pas les poser — et leur refus n'emporte jamais
 * le tableau ni son reçu.
 *
 * `borders: "none"` (#1861) n'est PAS du confort : c'est ce que l'avocat a
 * demandé, un tableau invisible pour placer du texte côte à côte (expéditeur et
 * destinataire d'une lettre). `create.table` pose six traits par défaut
 * (mesuré : `tests/ws-mocked/mesure-tableau-sans-bordure.spec.ts`), donc un
 * retrait manqué laisse un cadre que personne n'a demandé. Le retrait est
 * exigé AVANT toute écriture (`tables.applyBorderPreset` absent = refus sans
 * rien créer) et RELU après. Le reçu porte alors le contrôle
 * `table_borders_removed`, à trois issues — conforme, différent, non lisible —
 * et ne dit « sans bordure » que sur la première
 * (`controleDuRetraitDesBordures`).
 */
export async function executeCreateTable(
  ctx: ContexteDeMiseEnForme,
  input: Extract<CanvasActionInput, { action: 'create_table' }>,
): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  const placement = await resoudreLePlacement(ctx, input.placement, 'create_table');
  if (!placement.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: placement.code,
      message: placement.message,
    });
  }
  const lignes = input.rows.length;
  const colonnes = input.rows[0]?.length ?? 0;

  if (typeof ctx.doc.create?.table !== 'function'
    || typeof ctx.doc.tables?.setCellText !== 'function'
    || typeof ctx.doc.tables?.get !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'create_table exige editor.doc.create.table, editor.doc.tables.setCellText et editor.doc.tables.get, '
        + 'que cet éditeur n\'expose pas tous les trois. Rien n\'a été écrit et relancer la même action échouera à '
        + 'l\'identique. Pose le contenu en paragraphes (insert_paragraphs) : un tableau simulé par du texte tabulé '
        + 'ne serait pas un tableau à l\'export Word.',
    });
  }
  const sansBordure = input.borders === 'none';
  if (sansBordure && typeof ctx.doc.tables.applyBorderPreset !== 'function') {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'CAPABILITY_UNAVAILABLE',
      message: 'create_table borders:"none" exige editor.doc.tables.applyBorderPreset, que cet éditeur n\'expose pas : '
        + 'le tableau serait créé AVEC son cadre. Rien n\'a été écrit et relancer la même action échouera à '
        + 'l\'identique. Rappelle create_table sans borders si un tableau quadrillé convient ; sinon dis à l\'avocat '
        + 'que le tableau sans bordure n\'est pas disponible dans ce document.',
    });
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const revisionDeCreation = await revisionCourante(ctx, pre.revision);
  const creation = await muterSousFences(
    ctx,
    'create_table:create.table',
    { changeMode: modeDeRevision(ctx), expectedRevision: revisionDeCreation },
    () => ctx.doc.create?.table?.({ rows: lignes, columns: colonnes, at: placement.at }, {
      changeMode: modeDeRevision(ctx),
      expectedRevision: revisionDeCreation,
    }),
  );
  const nodeId = nodeIdCree(creation, 'table');
  if (nodeId === undefined) {
    const postSansTable = await snapshot(ctx);
    return buildVerifiedReceipt({
      intent: input.action,
      regimeSuivi: ctx.regimeSuivi,
      preSnapshot: toCanvasSnapshotSummary(pre),
      postSnapshot: toCanvasSnapshotSummary(postSansTable),
      resolvedTargets: placement.ancre === undefined ? [] : [targetSummary(placement.ancre)],
      provider: creation,
      revisionsAvant,
      revisionsApres: idsDeRevision(postSansTable.trackedChanges),
      verification: [{
        name: 'table_created',
        passed: false,
        message: 'L\'éditeur n\'a pas rendu l\'identifiant du tableau créé, donc aucune cellule n\'a pu être remplie. '
          + 'Relis le document par canvas_inspect avant toute autre action : un tableau vide a peut-être été posé.',
      }],
      effects: [{ kind: 'create_table', reviewability: 'mixed', description: 'Tableau créé sans identifiant lisible ; contenu non posé.' }],
    });
  }

  // ⚠️ La géométrie est relue AVANT de remplir : remplir une table dont la
  // forme n'est pas celle demandée écrirait le contenu aux mauvaises places, et
  // le rattrapage coûterait une suppression dans le document de l'avocat.
  const geometrie = await ctx.doc.tables.get({ nodeId });
  const relue = typeof geometrie === 'object' && geometrie !== null
    ? { lignes: (geometrie as { rows?: unknown }).rows, colonnes: (geometrie as { columns?: unknown }).columns }
    : { lignes: undefined, colonnes: undefined };
  const geometrieConforme = relue.lignes === lignes && relue.colonnes === colonnes;

  const cellulesEnEchec: string[] = [];
  // Géométrie non conforme : rien n'est rempli NI habillé. `non_tente` le fait
  // dire au reçu — le tableau posé porte alors son cadre par défaut.
  let retrait: RetraitDesBordures = sansBordure ? { etat: 'non_tente' } : { etat: 'non_demande' };
  if (geometrieConforme) {
    for (const [indexLigne, ligne] of input.rows.entries()) {
      for (const [indexColonne, texte] of ligne.entries()) {
        if (texte.trim().length === 0) continue;
        const attendue = await revisionCourante(ctx, pre.revision);
        const pose = await muterSousFences(
          ctx,
          `create_table:setCellText:${indexLigne},${indexColonne}`,
          { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
          () => ctx.doc.tables?.setCellText?.({ nodeId, rowIndex: indexLigne, columnIndex: indexColonne, text: texte }, {
            changeMode: modeDeRevision(ctx),
            expectedRevision: attendue,
          }),
        );
        if (estUnRefus(pose)) cellulesEnEchec.push(`(${indexLigne + 1},${indexColonne + 1})`);
      }
    }
    retrait = await habillerLaTable(ctx, nodeId, pre.revision, sansBordure);
  }
  const retraitDesBordures = retrait.etat === 'non_demande'
    ? undefined
    : controleDuRetraitDesBordures(
        nodeId,
        retrait,
        retrait.etat === 'non_tente' ? { etat: 'indisponible' } : await relireLesBordures(ctx, nodeId),
      );

  const post = await snapshot(ctx);
  ctx.verifierEpoque('create_table:receipt');

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: placement.ancre === undefined ? [] : [targetSummary(placement.ancre)],
    provider: creation,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    verification: [
      {
        name: 'table_geometry',
        passed: geometrieConforme,
        message: geometrieConforme
          ? `Le tableau relu porte bien ${lignes} lignes et ${colonnes} colonnes.`
          : `Le tableau a été annoncé créé, mais la relecture rend ${String(relue.lignes)}×${String(relue.colonnes)} `
            + `au lieu de ${lignes}×${colonnes} : les cellules n'ont PAS été remplies, pour ne pas écrire le contenu `
            + 'aux mauvaises places. Relis le document et supprime le tableau avant de réessayer.',
      },
      {
        name: 'table_cells',
        passed: cellulesEnEchec.length === 0,
        message: cellulesEnEchec.length === 0
          ? 'Chaque cellule non vide a été remplie.'
          : `L'éditeur a refusé de remplir les cellules ${cellulesEnEchec.join(', ')} ; le tableau et les autres `
            + 'cellules sont en place. Complète-les à la main ou signale-le à l\'avocat.',
      },
      ...(retraitDesBordures === undefined ? [] : [retraitDesBordures]),
    ],
    effects: [{
      kind: 'create_table',
      // `mixed` et pas `tracked` : le CADRE du tableau produit une révision,
      // son CONTENU n'en produit aucune (mesuré). Le receipt ne rabat jamais
      // `mixed`, ce qui est exactement l'effet voulu ici.
      reviewability: 'mixed',
      // « sans bordure » ne s'écrit que sur un retrait RELU conforme : le dire
      // sur la seule demande ferait annoncer à l'avocat un cadre invisible qu'il
      // verrait à l'écran.
      description: `Tableau ${lignes}×${colonnes} ${
        retraitDesBordures === undefined
          ? 'avec bordures'
          : retraitDesBordures.passed
            ? 'sans bordure'
            : 'dont le retrait des bordures est NON confirmé (contrôle table_borders_removed)'
      } créé et rempli en texte brut (les cellules ne portent ni gras, ni italique, ni lien).`,
      nodeIds: [nodeId],
    }],
  });
}

/**
 * Ce que l'habillage a fait du retrait des bordures demandé par `borders:"none"`.
 *
 * Quatre issues distinctes, parce que le reçu ne les dit pas pareil :
 * `applique` (l'éditeur a accepté — ce qui ne prouve rien, la relecture
 * tranche), `refuse` (refus rendu ou appel qui a jeté, avec sa cause),
 * `non_tente` (habillage sauté : géométrie non conforme) et `non_demande`
 * (tableau quadrillé, aucun contrôle à émettre).
 *
 * « Préréglage absent de l'éditeur » n'est pas une issue : `executeCreateTable`
 * le refuse avant toute écriture.
 */
export type RetraitDesBordures =
  | { etat: 'non_demande' }
  | { etat: 'non_tente' }
  | { etat: 'applique' }
  | { etat: 'refuse'; detail: string };

/**
 * L'habillage du tableau créé : son cadre, puis la pleine largeur.
 *
 * Un refus n'emporte jamais le tableau : faire échouer le verbe sur l'habillage
 * transformerait une différence d'apparence en perte de contenu. Il est donc
 * avalé ici, et seulement ici — celui du retrait des bordures est RENDU à
 * l'appelant, qui en répond dans le reçu.
 *
 * ⚠️ **Écriture DIRECTE, quel que soit le régime de suivi** (règle n°1 de la
 * spec #1858 : la forme ne crée pas de révision). Mesuré le 2026-10-02
 * (`mesure-tableau-sans-bordure.spec.ts`) : demandés en `tracked` sous suivi
 * actif, `applyBorderPreset` comme `setStyle` + `setLayout` laissent chacun une
 * révision de forme (`w:tblPrChange`) que l'avocat n'a pas demandée ; en
 * `direct`, aucune.
 */
async function habillerLaTable(
  ctx: ContexteDeMiseEnForme,
  nodeId: string,
  repli: RevisionDeDocument,
  sansBordure: boolean,
): Promise<RetraitDesBordures> {
  /** `undefined` : accepté, ou surface absente. Sinon la cause du refus. */
  const essayer = async (nom: string, methode: MethodeProvider | undefined, entree: Record<string, unknown>): Promise<string | undefined> => {
    if (typeof methode !== 'function') return undefined;
    try {
      const attendue = await revisionCourante(ctx, repli);
      const resultat = await muterSousFences(ctx, `create_table:${nom}`, { changeMode: 'direct', expectedRevision: attendue },
        () => methode(entree, { changeMode: 'direct', expectedRevision: attendue }));
      return estUnRefus(resultat) ? detailDuRefus(resultat) : undefined;
    } catch (erreur) {
      if (estArretDeLot(erreur)) throw erreur;
      console.warn(`[create_table] habillage ${nom} refusé sur ${nodeId}`, erreur);
      return (erreur instanceof Error ? `${erreur.name}: ${erreur.message}` : String(erreur)).slice(0, 200);
    }
  };
  // `create.table` pose déjà six traits : `TableGrid` nomme le style du tableau
  // quadrillé, le préréglage `none` retire les traits. L'un ou l'autre, jamais
  // les deux — un tableau invisible n'a pas à porter un style de grille.
  // Liées à leur famille : `essayer` les appelle nues, et une opération détachée perd le receveur qu'elle lit — elle lève,
  // le `catch` en fait un refus, et l'habillage est perdu (revue de la PR #2257).
  let retrait: RetraitDesBordures = { etat: 'non_demande' };
  if (sansBordure) {
    const refus = await essayer('tables.applyBorderPreset', operationDuDocument(ctx.doc, 'tables', 'applyBorderPreset'), { nodeId, preset: 'none' });
    retrait = refus === undefined ? { etat: 'applique' } : { etat: 'refuse', detail: refus };
  } else {
    await essayer('tables.setStyle', operationDuDocument(ctx.doc, 'tables', 'setStyle'), { nodeId, styleId: 'TableGrid' });
  }
  await essayer('tables.setLayout', operationDuDocument(ctx.doc, 'tables', 'setLayout'), { nodeId, autoFitMode: 'fitWindow' });
  return retrait;
}

/** Ce que la relecture des propriétés du tableau a rendu — jamais une exception. */
export type RelectureDesBordures =
  | { etat: 'lue'; proprietes: unknown }
  | { etat: 'indisponible' }
  | { etat: 'jetee'; detail: string };

/**
 * Relit les propriétés du tableau par le MODÈLE (`tables.getProperties`).
 *
 * La source unique de lecture de la forme (fichier exporté, lecteur de forme —
 * #1859) viendra se brancher ici.
 */
async function relireLesBordures(ctx: ContexteDeMiseEnForme, nodeId: string): Promise<RelectureDesBordures> {
  // Liée à sa famille : détachée, la lecture lève faute de receveur, et un tableau sans bordure se dit non relu.
  const lire = operationDuDocument(ctx.doc, 'tables', 'getProperties');
  if (lire === undefined) return { etat: 'indisponible' };
  try {
    return { etat: 'lue', proprietes: await lire({ nodeId }) };
  } catch (erreur) {
    if (estArretDeLot(erreur)) throw erreur;
    return { etat: 'jetee', detail: (erreur instanceof Error ? `${erreur.name}: ${erreur.message}` : String(erreur)).slice(0, 200) };
  }
}

/** Les six côtés du cadre d'un tableau, tels que `tables.getProperties` les nomme. */
const COTES_DU_CADRE = ['top', 'bottom', 'left', 'right', 'insideH', 'insideV'] as const;

/**
 * Les côtés qui portent ENCORE un trait, d'après `tables.getProperties`.
 *
 * Mesuré le 2026-10-02 : après `applyBorderPreset({preset:'none'})`, `borders`
 * vaut `{top:null, …}` sur les six côtés — et le fichier exporté porte alors
 * `w:val="nil"` partout ; un côté tracé vaut `{lineStyle:'single', …}`.
 *
 * ⚠️ `undefined` dès qu'UN côté n'est pas lisible — objet `borders` absent, côté
 * absent de la réponse, forme inconnue. Seul `null` est mesuré comme « pas de
 * trait » : un côté que l'éditeur ne publie pas n'est pas un côté sans trait.
 * Un côté rendu `{lineStyle:'none'|'nil'}` n'a jamais été observé : il n'est ni
 * compté comme un trait (le reçu échouerait sur un tableau peut-être conforme)
 * ni tenu pour conforme — non lisible, jusqu'à mesure.
 */
function cotesEncoreTraces(proprietes: unknown): string[] | undefined {
  if (typeof proprietes !== 'object' || proprietes === null) return undefined;
  const bordures = (proprietes as { borders?: unknown }).borders;
  if (typeof bordures !== 'object' || bordures === null) return undefined;
  const traces: string[] = [];
  for (const cote of COTES_DU_CADRE) {
    const trait = (bordures as Record<string, unknown>)[cote];
    if (trait === null) continue;
    const style = typeof trait === 'object' ? (trait as { lineStyle?: unknown }).lineStyle : undefined;
    if (typeof style !== 'string' || style === 'none' || style === 'nil') return undefined;
    traces.push(cote);
  }
  return traces;
}

/**
 * Le contrôle `table_borders_removed` — la postcondition de `borders:"none"`.
 *
 * Fonction PURE : elle décide sur ce que l'éditeur a rendu, pour que les trois
 * issues se testent sans éditeur (`formatActionExecutors.test.ts`).
 *
 * - **conforme** : six côtés relus sans trait. Seule issue `passed:true`.
 * - **différent** : au moins un trait relu ; le message nomme les côtés.
 * - **non lisible** : relecture absente, en échec ou incomplète. Jamais
 *   « conforme » : l'acceptation du préréglage ne prouve rien (règle n°4 de la
 *   spec #1858).
 *
 * @param retrait - Ce que l'habillage a fait du retrait ; jamais `non_demande`.
 */
/** Le nom du contrôle de `borders:"none"`, lu aussi par `superDocCanvasExecutor`. */
export const CONTROLE_DU_RETRAIT_DES_BORDURES = 'table_borders_removed';

export function controleDuRetraitDesBordures(
  nodeId: string,
  retrait: Exclude<RetraitDesBordures, { etat: 'non_demande' }>,
  relecture: RelectureDesBordures,
): VerificationCheck {
  const name = CONTROLE_DU_RETRAIT_DES_BORDURES;
  // Ce qu'on fait dire à l'avocat suit ce qui est SU : traits relus → le cadre
  // est visible ; retrait refusé et non relu → probablement ; retrait accepté
  // et non relu → rien n'autorise à annoncer un cadre visible.
  const sansRecreer = (aDireALAvocat: string): string => 'Ne recrée pas le tableau, tu en poserais un second. Aucun '
    + `verbe ne retire les bordures d'un tableau existant : dis à l'avocat que ${aDireALAvocat} ; il peut retirer `
    + 'les bordures dans Word (Bordures → Aucune).';
  if (retrait.etat === 'non_tente') {
    return {
      name,
      passed: false,
      message: `Le retrait des bordures du tableau ${nodeId} n'a PAS été tenté : sa géométrie relue n'est pas celle `
        + 'demandée, donc il n\'a été ni rempli ni habillé. S\'il existe, il porte son cadre par défaut. Relis le '
        + 'document et supprime ce tableau avant de réessayer.',
    };
  }
  const reponse = retrait.etat === 'refuse'
    ? `l'éditeur a REFUSÉ le retrait (${retrait.detail})`
    : 'l\'éditeur a accepté le retrait';
  const restants = relecture.etat === 'lue' ? cotesEncoreTraces(relecture.proprietes) : undefined;
  if (restants === undefined) {
    const pourquoi = relecture.etat === 'indisponible'
      ? 'cet éditeur n\'expose pas tables.getProperties'
      : relecture.etat === 'jetee'
        ? `tables.getProperties a échoué (${relecture.detail})`
        : 'tables.getProperties n\'a pas rendu les six côtés de borders';
    return {
      name,
      passed: false,
      message: `Non relu : le tableau ${nodeId} est créé et rempli, ${reponse}, mais ses bordures n'ont pas pu être `
        + `relues — ${pourquoi}. Leur absence n'est donc PAS prouvée. ${sansRecreer(retrait.etat === 'refuse'
          ? 'le cadre est probablement resté visible'
          : 'le retrait des bordures n\'est pas confirmé')}`,
    };
  }
  if (restants.length === 0) {
    return { name, passed: true, message: `Relecture : le tableau ${nodeId} ne porte plus aucune bordure (six côtés sans trait).` };
  }
  return {
    name,
    passed: false,
    message: `Le tableau ${nodeId} est créé et rempli, mais la relecture montre encore un trait sur : `
      + `${restants.join(', ')} — ${reponse}. ${sansRecreer('le cadre est resté visible')}`,
  };
}
